<template>
  <div class="schedule-page">
    <div class="header" :style="headerStyle">
      <div class="header-hotel">
        <img
          v-if="brandUrl"
          :src="brandUrl"
          class="hotel-logo"
          @error="brandUrl = ''"
        />
        <span class="hotel-name">{{ hotelName }}</span>
      </div>
    </div>

    <van-form @submit="onSubmit" class="request-form">
      <!-- Employee Name -->
      <van-field
        v-model="form.employeeName"
        label="Your Name"
        placeholder="Enter your name"
        :readonly="isNameLocked"
        :rules="[{ required: true, message: 'Name is required' }]"
      />

      <!-- Department -->
      <van-field
        readonly
        clickable
        label="Department"
        :value="selectedDeptName"
        placeholder="Select department"
        :rules="[{ required: true, message: 'Please select a department' }]"
        @click="showDeptPicker = true"
      />
      <van-popup v-model="showDeptPicker" position="bottom" round>
        <van-picker
          show-toolbar
          :columns="deptColumns"
          @confirm="onDeptConfirm"
          @cancel="showDeptPicker = false"
        />
      </van-popup>

      <!-- Request Type -->
      <van-field
        readonly
        clickable
        label="Request Type"
        :value="selectedTypeLabel"
        placeholder="Select request type"
        :rules="[{ required: true, message: 'Please select request type' }]"
        @click="showTypePicker = true"
      />
      <van-popup v-model="showTypePicker" position="bottom" round>
        <van-picker
          show-toolbar
          :columns="typeColumns"
          @confirm="onTypeConfirm"
          @cancel="showTypePicker = false"
        />
      </van-popup>

      <!-- On Call Status -->
      <van-field label="On Call">
        <template #input>
          <van-radio-group v-model="form.onCall" direction="horizontal">
            <van-radio name="yes" :checked-color="submitColor">Yes</van-radio>
            <van-radio name="no" :checked-color="submitColor">No</van-radio>
          </van-radio-group>
        </template>
      </van-field>

      <!-- Date Range -->
      <van-field
        readonly
        clickable
        label="Start Date"
        :value="form.startDate"
        placeholder="Select start date"
        :rules="[{ required: true, message: 'Start date is required' }]"
        @click="showStartDate = true"
      />
      <van-calendar
        v-model="showStartDate"
        @confirm="onStartDateConfirm"
        :min-date="minDate"
        :max-date="maxDate"
        color="#1989fa"
      />

      <van-field
        readonly
        clickable
        label="End Date"
        :value="form.endDate"
        placeholder="Same as start date (optional)"
        @click="showEndDate = true"
      />
      <van-calendar
        v-model="showEndDate"
        @confirm="onEndDateConfirm"
        :min-date="endDateMin"
        :max-date="maxDate"
        color="#1989fa"
      />

      <!-- Time (optional) -->
      <van-field
        readonly
        clickable
        label="Start Time"
        :value="form.startTime"
        placeholder="Optional"
        @click="showStartTime = true"
      />
      <van-popup v-model="showStartTime" position="bottom" round>
        <van-datetime-picker
          type="time"
          @confirm="onStartTimeConfirm"
          @cancel="showStartTime = false"
        />
      </van-popup>

      <van-field
        readonly
        clickable
        label="End Time"
        :value="form.endTime"
        placeholder="Optional"
        @click="showEndTime = true"
      />
      <van-popup v-model="showEndTime" position="bottom" round>
        <van-datetime-picker
          type="time"
          @confirm="onEndTimeConfirm"
          @cancel="showEndTime = false"
        />
      </van-popup>

      <!-- Notes -->
      <van-field
        v-model="form.notes"
        label="Notes"
        type="textarea"
        placeholder="Any additional details..."
        rows="3"
        autosize
      />

      <!-- Submit -->
      <div class="submit-area">
        <van-button round block plain type="info" style="margin-bottom: 12px" @click="goToRecords">
          View My Requests
        </van-button>
        <van-button round block type="info" native-type="submit" :loading="submitting" :color="submitColor">
          Submit
        </van-button>
      </div>
    </van-form>

    <div class="powered-by">Powered by Hospitality Voice Tech Inc.</div>
  </div>
</template>

<script>
import {
  getHotelInfo,
  getDepartments,
  submitScheduleRequest,
  getCurrentScheduleUser,
  getScheduleUserInfo,
} from "@/api/schedule";
import { Toast } from "vant";
import {
  getScheduleAuthToken,
  getScheduleAuthUserId,
  setScheduleAuthUserId,
  clearScheduleAuthSession,
} from "@/utils/scheduleAuth";

const REQUEST_TYPES = [
  { value: "day_off", label: "Day Off" },
  { value: "early_leave", label: "Early Leave" },
  { value: "vacation", label: "Vacation" },
  { value: "overtime", label: "Sign Up for Overtime" },
];

function formatDateValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default {
  name: "ScheduleRequest",
  data() {
    const now = new Date();
    const today = formatDateValue(now);
    return {
      hotelNo: null,
      hotelName: "",
      brandUrl: "",
      hotelColor: "",
      departments: [],
      isNameLocked: false,
      requesterUserId: null,
      form: {
        employeeName: "",
        hotelDeptId: null,
        requestType: "",
        onCall: "yes",
        startDate: today,
        endDate: today,
        startTime: "",
        endTime: "",
        notes: "",
      },
      selectedDeptName: "",
      selectedTypeLabel: "",
      showDeptPicker: false,
      showTypePicker: false,
      showStartDate: false,
      showEndDate: false,
      showStartTime: false,
      showEndTime: false,
      submitting: false,
      minDate: now,
      maxDate: new Date(now.getFullYear() + 1, 11, 31),
    };
  },
  computed: {
    headerStyle() {
      if (this.hotelColor) {
        return {
          background: `#${this.hotelColor}`,
        };
      }
      return {};
    },
    submitColor() {
      return this.hotelColor ? `#${this.hotelColor}` : '#1989fa';
    },
    deptColumns() {
      return this.departments.map((d) => d.name);
    },
    typeColumns() {
      return REQUEST_TYPES.map((t) => t.label);
    },
    endDateMin() {
      if (this.form.startDate) {
        return new Date(this.form.startDate);
      }
      return this.minDate;
    },
  },
  async created() {
    this.hotelNo = this.$route.query.hotel;
    if (!this.hotelNo) {
      Toast.fail("Missing hotel information. Please scan the QR code again.");
      return;
    }
    await this.initializePage();
  },
  methods: {
    async initializePage() {
      await Promise.all([this.loadHotelInfo(), this.loadDepartments()]);
      if (this.$route.query.user) {
        await this.prefillByUserId(this.$route.query.user);
        return;
      }
      if (this.$route.query.login === "1") {
        await this.prefillByLoginSession();
      }
    },
    async loadHotelInfo() {
      try {
        const res = await getHotelInfo(this.hotelNo);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess && res.data) {
          this.hotelName = res.data.hotelName || "";
          this.hotelColor = res.data.color || "";
          if (res.data.brand) {
            this.brandUrl =
              "https://hotelfile353485474178.s3-accelerate.amazonaws.com/logo/" +
              res.data.brand.toLowerCase().replace(/ /g, "_") +
              ".png";
          }
        }
      } catch (e) {
        // Non-critical — header will show default
      }
    },
    async loadDepartments() {
      try {
        const res = await getDepartments(this.hotelNo);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess) {
          this.departments = res.data || [];
          this.syncSelectedDepartment();
        } else {
          Toast.fail(res.msg || "Failed to load departments");
        }
      } catch (e) {
        Toast.fail("Network error. Please try again.");
      }
    },
    syncSelectedDepartment() {
      if (!this.form.hotelDeptId) {
        return;
      }
      const dept = this.departments.find((item) => String(item.id) === String(this.form.hotelDeptId));
      if (dept) {
        this.selectedDeptName = dept.name;
      }
    },
    applyUserPrefill(userInfo) {
      this.isNameLocked = true;
      this.form.employeeName = userInfo.employeeName || "";
      this.form.hotelDeptId = userInfo.hotelDeptId || null;
      this.selectedDeptName = userInfo.hotelDeptName || "";
      this.syncSelectedDepartment();
    },
    async prefillByUserId(userId) {
      try {
        const res = await getScheduleUserInfo(this.hotelNo, userId);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess && res.data) {
          this.requesterUserId = userId;
          this.applyUserPrefill(res.data);
          return;
        }
        Toast.fail(res.msg || "Failed to load user information.");
      } catch (e) {
        Toast.fail("Failed to load user information.");
      }
    },
    async prefillByLoginSession() {
      try {
        let userId = getScheduleAuthUserId();
        if (!userId) {
          const token = getScheduleAuthToken();
          if (!token) {
            Toast.fail("Login session expired. Please log in again.");
            this.redirectToLogin();
            return;
          }
          const currentUserRes = await getCurrentScheduleUser(token);
          const isSuccess = Number(currentUserRes.status) === 0 || currentUserRes.success === true;
          userId = currentUserRes.data && currentUserRes.data.id;
          if (!isSuccess || !userId) {
            clearScheduleAuthSession();
            Toast.fail(currentUserRes.msg || "Login session expired. Please log in again.");
            this.redirectToLogin();
            return;
          }
          setScheduleAuthUserId(userId);
        }
        await this.prefillByUserId(userId);
      } catch (e) {
        clearScheduleAuthSession();
        Toast.fail("Login session expired. Please log in again.");
        this.redirectToLogin();
      }
    },
    redirectToLogin() {
      this.$router.replace({
        name: "ScheduleLogin",
        query: {
          hotel: this.hotelNo,
        },
      });
    },
    buildRecordsRouteQuery() {
      const query = {
        hotel: this.hotelNo,
      };
      if (this.$route.query.user) {
        query.user = this.$route.query.user;
        return query;
      }
      if (this.$route.query.login === "1") {
        query.login = "1";
        return query;
      }
      query.employeeName = (this.form.employeeName || "").trim();
      query.hotelDeptId = this.form.hotelDeptId;
      query.deptName = this.selectedDeptName || "";
      return query;
    },
    goToRecords() {
      if (!this.requesterUserId) {
        if (!this.form.employeeName || !this.form.employeeName.trim() || !this.form.hotelDeptId) {
          Toast.fail("Please enter your name and select a department first.");
          return;
        }
      }
      this.$router.push({
        name: "ScheduleRecords",
        query: this.buildRecordsRouteQuery(),
      });
    },
    onDeptConfirm(value, index) {
      const dept = this.departments[index];
      this.form.hotelDeptId = dept.id;
      this.selectedDeptName = dept.name;
      this.showDeptPicker = false;
    },
    onTypeConfirm(value, index) {
      this.form.requestType = REQUEST_TYPES[index].value;
      this.selectedTypeLabel = REQUEST_TYPES[index].label;
      this.showTypePicker = false;
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    onStartDateConfirm(date) {
      this.form.startDate = this.formatDate(date);
      this.showStartDate = false;
      // Reset end date if it's before start date
      if (this.form.endDate && this.form.endDate < this.form.startDate) {
        this.form.endDate = "";
      }
    },
    onEndDateConfirm(date) {
      this.form.endDate = this.formatDate(date);
      this.showEndDate = false;
    },
    onStartTimeConfirm(value) {
      this.form.startTime = value;
      this.showStartTime = false;
    },
    onEndTimeConfirm(value) {
      this.form.endTime = value;
      this.showEndTime = false;
    },
    async onSubmit() {
      this.submitting = true;
      try {
        const payload = {
          hotelNo: this.hotelNo,
          requesterUserId: this.requesterUserId || null,
          employeeName: this.form.employeeName.trim(),
          hotelDeptId: this.form.hotelDeptId,
          requestType: this.form.requestType,
          onCall: this.form.onCall === "yes",
          startDate: this.form.startDate,
          endDate: this.form.endDate || this.form.startDate,
          startTime: this.form.startTime || null,
          endTime: this.form.endTime || null,
          notes: this.form.notes.trim() || null,
        };
        const res = await submitScheduleRequest(payload);
        const isSuccess = Number(res.status) === 0 || res.success === true;
        if (isSuccess) {
          this.$router.push({
            name: "SubmitSuccess",
            query: {
              ...this.buildRecordsRouteQuery(),
              msg: res.msg || res.data,
            },
          });
        } else {
          Toast.fail(res.msg || "Submission failed. Please try again.");
        }
      } catch (e) {
        Toast.fail("Network error. Please try again.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.schedule-page {
  padding: 0 0 30px 0;
}

.header {
  background: #1989fa;
  color: #fff;
  padding: 14px 16px 12px;

  .header-hotel {
    display: flex;
    align-items: center;
  }

  .hotel-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
    margin-right: 8px;
    border-radius: 4px;
    background: #fff;
    padding: 2px;
    flex-shrink: 0;
  }

  .hotel-name {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    word-break: normal;
  }

  .header-title {
    font-size: 17px;
    font-weight: 600;
    text-align: center;
    margin-top: 8px;
  }
}

.request-form {
  margin: 16px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.submit-area {
  padding: 20px 16px;
}

.powered-by {
  text-align: center;
  font-size: 11px;
  color: #999;
  padding: 16px 0 0;
}
</style>

<style lang="less">
.van-toast {
  word-break: normal;
  overflow-wrap: break-word;
}
</style>
